import { callApi, executeActionsResponse, executeActionsError, getToken } from './apiMiddlewareCommon';
import type { CallApiPayload } from './apiMiddlewareCommon';
import type { Middleware } from 'redux';

export const CALL_API_SEQUENCED = 'CALL_API_SEQUENCED';

// Tracks, per sequencing key, the sequence number of the most recently
// *dispatched* call for that key. A response only gets to run its
// success/error actions if it's still the latest call issued for its key
// by the time it settles - "last one dispatched wins", not "last one to
// respond wins". This fixes the out-of-order-response race documented in
// SYSTEM.debt.md §15.29: an older fetchStudentCourse response landing
// after a newer one and overwriting fresher progress in the store with
// stale data (e.g. progress:0 for a lesson already finished).
//
// Deliberately a separate CALL_API_SEQUENCED action type (and middleware)
// rather than changing the shared CALL_API/ApiMiddleware used everywhere
// else - this behavior is opt-in per call site instead of a blanket change
// to every consumer of apiActionCreator.
const latestSequenceByKey = new Map<string, number>();

const ApiMiddlewareSequenced: Middleware = () => (next) => (action) => {
  const callAPI: CallApiPayload | undefined = action[CALL_API_SEQUENCED];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }
  const {
    endpoint,
    types,
    authenticated,
    method,
    data,
    microservice,
    contentType,
    accept,
    customToken,
    // Calls that share a sequenceKey compete with each other; defaults to
    // the endpoint, which is enough for a fetch whose URL already embeds
    // the resource id (e.g. `/student/course/${courseId}`).
    sequenceKey = endpoint,
  } = callAPI;

  const { requestType, successTypes, errorTypes } = types;

  if (typeof requestType === 'object') {
    next(requestType);
  } else if (typeof requestType === 'function') {
    next(requestType());
  } else {
    next({
      type: requestType,
    });
  }

  const sequence = (latestSequenceByKey.get(sequenceKey) || 0) + 1;
  latestSequenceByKey.set(sequenceKey, sequence);

  const token = customToken ?? getToken(authenticated);
  const isStale = () => latestSequenceByKey.get(sequenceKey) !== sequence;

  return callApi({
    endpoint,
    authenticated,
    microservice,
    method,
    data,
    token,
    next,
    contentType,
    accept,
  })
    .then((response: any) => {
      if (isStale()) {
        return undefined;
      }
      return executeActionsResponse(next, response, successTypes);
    })
    .catch((error: any) => {
      if (isStale()) {
        return undefined;
      }
      return executeActionsError(next, error, errorTypes);
    });
};

export default ApiMiddlewareSequenced;
